import React, { useCallback, useEffect, useState } from "react";
import { FaCheck, FaImage, FaSync, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api/httpClient";
import { API_PATHS } from "../../api/apiPaths";
import { useAppTheme } from "../../context/AppThemeContext";

const extractRequests = (response) => {
  const payload = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(payload)) return payload;
  return payload?.requests || payload?.content || payload?.items || [];
};

const requestIdOf = (request) => request?.requestId || request?.id || request?._id || "";
const formatTimestamp = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

export default function SuperAdminBrandingApprovals() {
  const { isDarkMode } = useAppTheme();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(API_PATHS.superAdmin.tenantBrandingChangeRequests(), {
        cache: "no-store",
        withCredentials: true,
      });
      setRequests(extractRequests(response));
    } catch (error) {
      toast.error(error?.message || "Unable to load logo approval requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const review = async (request, decision) => {
    const requestId = requestIdOf(request);
    if (!requestId || updatingId) return;
    const action = decision === "approve" ? "approve" : "reject";
    if (!window.confirm(`${action === "approve" ? "Approve" : "Reject"} this school logo replacement?`)) return;

    setUpdatingId(requestId);
    try {
      const endpoint = action === "approve"
        ? API_PATHS.superAdmin.approveTenantBrandingChangeRequest(requestId)
        : API_PATHS.superAdmin.rejectTenantBrandingChangeRequest(requestId);
      await api.post(endpoint, undefined, { withCredentials: true });
      setRequests((current) => current.filter((item) => requestIdOf(item) !== requestId));
      toast.success(action === "approve" ? "School logo replacement approved." : "School logo replacement rejected.");
    } catch (error) {
      toast.error(error?.message || `Unable to ${action} this logo replacement.`);
    } finally {
      setUpdatingId("");
    }
  };

  const surface = isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900";

  return (
    <main className={`mx-auto max-w-7xl p-4 sm:p-6 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Tenant branding</p>
          <h1 className="mt-2 text-3xl font-bold">Logo Approvals</h1>
          <p className="mt-1 text-sm text-slate-500">Review replacement logos while each school’s currently approved logo remains live.</p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold disabled:cursor-wait disabled:opacity-60"><FaSync className={loading ? "animate-spin" : ""} /> Refresh</button>
      </div>

      <section className={`mt-6 overflow-hidden rounded-2xl border ${surface}`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px] text-sm">
            <thead className={isDarkMode ? "bg-slate-950 text-slate-300" : "bg-slate-100 text-slate-600"}>
              <tr><th className="px-4 py-3 text-left">School</th><th className="px-4 py-3 text-left">Requested by</th><th className="px-4 py-3 text-left">Submitted</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-right">Review</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-500">Loading logo requests...</td></tr>
                : requests.length === 0 ? <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-500"><FaImage className="mx-auto mb-3 text-2xl" />No pending logo replacements.</td></tr>
                  : requests.map((request) => {
                    const id = requestIdOf(request);
                    const busy = updatingId === id;
                    const status = String(request.status || "PENDING").toUpperCase();
                    return <tr key={id} className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                      <td className="px-4 py-4 font-semibold">{request.schoolName || request.tenantName || request.tenantId || "School"}</td>
                      <td className="px-4 py-4">{request.requestedByName || request.requestedBy || request.requestedByEmail || "School admin"}</td>
                      <td className="px-4 py-4 text-slate-500">{formatTimestamp(request.createdAt || request.requestedAt || request.submittedAt)}</td>
                      <td className="px-4 py-4"><span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700">{status}</span></td>
                      <td className="px-4 py-4">{status === "PENDING" ? <div className="flex justify-end gap-2"><button type="button" disabled={busy} onClick={() => review(request, "approve")} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-60"><FaCheck /> Approve</button><button type="button" disabled={busy} onClick={() => review(request, "reject")} className="inline-flex items-center gap-1 rounded-lg border border-rose-300 px-3 py-2 text-xs font-bold text-rose-600 disabled:opacity-60"><FaTimes /> Reject</button></div> : <span className="block text-right text-xs text-slate-500">Reviewed</span>}</td>
                    </tr>;
                  })}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
